import {
  CalendarPopupHeader,
  TimesIconWrapper,
} from '@/components_deprecated/Calendar/components/components'
import { PopupMenu } from '@/components_deprecated/PopupMenu'
import type { TableMenuItem } from '@/components_deprecated/Table'
import { Module } from '@/modules/companies'
import { format } from '@/utils/date-fns'
import { useAdmin, useModuleResponsible, usePermissions } from '@/utils/hooks'
import useClickOutside from '@/utils/hooks/useClickOutside'
import { PERMISSIONS } from '@/utils/permissions'
import { Icon } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useRef, useState } from 'react'
import styled from 'styled-components'
import type { AbsenceNode } from './absences-table'
import { AbsenceStatusDisplay } from './AbsenceStatusDisplay'
import { useOldAbsenceHandler } from './handler.hooks'
import { getAbsenceDisplayEnd } from '../util'

export const OuterPopupWrapper = styled.div`
  background-color: ${props => props.theme.colors.white};
  padding: 1rem;
  border-radius: ${props => props.theme.standardBorderRadius};
  box-shadow: ${props => props.theme.shadow.default};
`

const AbsenceCalendarPopupWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 340px;
`

const AbsenceCalendarPopupHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  height: 24px;
  padding-bottom: 2rem;
  line-height: 20px;
`

const AbsenceCalendarPopupHeaderButtons = styled.div`
  display: flex;
  gap: 1rem;
`

interface AbsenceCalendarPopupProps {
  absence: AbsenceNode
  onClickOutside: () => void
}

export const AbsenceCalendarPopup: React.FC<AbsenceCalendarPopupProps> = ({
  absence,
  onClickOutside,
}) => {
  const admin = useAdmin()

  const translations = useTranslate({
    editAbsence: 'timesheets.edit-absence',
    editAbsenceStatus: 'timesheets.edit-absence-status',
    deleteAbsence: 'timesheets.delete-absence',
    absenceType: 'timesheets.absence-type',
    reason: 'common.reason',

    prompt: {
      deleteAbsence: 'timesheets.prompt.delete-absence',
      deleteAbsenceTitle: 'timesheets.prompt.delete-absence-title',
    },
  })

  const { isResponsibleUser } = useModuleResponsible(Module.ABSENCES)

  const [promptActive, setPromptActive] = useState(false)

  const { hasPermissionsAndMe } = usePermissions()

  const canEdit = (id: string) =>
    admin || hasPermissionsAndMe({ id }, PERMISSIONS.timesheets.change.absence)
  const canDelete = (id: string) =>
    admin || hasPermissionsAndMe({ id }, PERMISSIONS.timesheets.delete.absence)
  const canEditAbsenceStatus = admin || isResponsibleUser

  const wrapperRef = useRef<HTMLDivElement | null>(null)

  const displayStartDate = format(new Date(absence.datetimeStart), 'EEEE dd.MM')
  const displayEndDate = format(getAbsenceDisplayEnd(absence), 'EEEE dd.MM')

  const absenceHandler = useOldAbsenceHandler()

  function handleDeleteAbsence() {
    void absenceHandler.delete(absence, setPromptActive)
    onClickOutside()
  }

  function createMenuItems(entry: AbsenceNode) {
    const items: TableMenuItem<AbsenceNode>[] = []

    if (canEdit(entry.user.id))
      items.push({
        label: translations.editAbsence,
        role: 'link',
        onClick: () => absenceHandler.edit(absence, setPromptActive),
      })
    if (canEditAbsenceStatus) {
      items.push({
        label: translations.editAbsenceStatus,
        role: 'link',
        onClick: () => absenceHandler.editStatus(absence, setPromptActive),
      })
    }
    if (canDelete(entry.user.id))
      items.push({
        label: translations.deleteAbsence,
        onClick: () => handleDeleteAbsence(),
      })
    return items
  }

  const menuItems = createMenuItems(absence)

  useClickOutside(wrapperRef, onClickOutside, true, !promptActive)

  return (
    <AbsenceCalendarPopupWrapper ref={wrapperRef}>
      <AbsenceCalendarPopupHeader>
        <AbsenceStatusDisplay status={absence.requestStatus} />

        <AbsenceCalendarPopupHeaderButtons>
          <PopupMenu items={menuItems} />

          <TimesIconWrapper>
            <Icon
              icon="times"
              type="light"
              size="1.6rem"
              color="gray5"
              onClick={onClickOutside}
            />
          </TimesIconWrapper>
        </AbsenceCalendarPopupHeaderButtons>
      </AbsenceCalendarPopupHeader>

      <CalendarPopupHeader>{absence.user.fullName}</CalendarPopupHeader>

      <p>
        {displayStartDate} - {displayEndDate}
      </p>

      <div>
        <b>{translations.absenceType}:</b>{' '}
        {absence.absenceType?.name ?? 'Unknown'}
      </div>

      <div>
        <b>{translations.reason}:</b> {absence.reason}
      </div>
    </AbsenceCalendarPopupWrapper>
  )
}
